import { useState, useMemo } from "react";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, federalIncomeTax } from "@/lib/finance";
import { fmtUSD, fmtPct } from "@/lib/format";

// OBBBA 2025 caps + phaseouts
// Tips deduction: up to $25,000 / year. Phaseout $150K single / $300K joint.
// Overtime deduction: up to $12,500 single / $25,000 joint. Phaseout $150K / $300K.
// Phaseout reduction: $100 per $1,000 MAGI over threshold (approx; same rule applied to both
// per current IRS guidance parallelism).
const TIPS_CAP_SINGLE = 25000;
const TIPS_CAP_JOINT = 25000;
const OT_CAP_SINGLE = 12500;
const OT_CAP_JOINT = 25000;
const PHASEOUT_START_SINGLE = 150000;
const PHASEOUT_START_JOINT = 300000;

// FICA: Social Security 6.2% (up to 2025 wage base ~$176,100) + Medicare 1.45%.
// Using flat 7.65% — close enough for typical hourly worker incomes.
const FICA_RATE = 0.0765;

function phaseoutReduce(cap, magi, threshold) {
    const over = Math.max(0, magi - threshold);
    const reduction = (over / 1000) * 100; // $100 per $1,000
    return Math.max(0, cap - reduction);
}

export default function OvertimeTipsPage() {
    const [hourly, setHourly] = useState(22);
    const [regHours, setRegHours] = useState(40);
    const [otHours, setOtHours] = useState(10);
    const [weeklyTips, setWeeklyTips] = useState(150);
    const [weeks, setWeeks] = useState(50);
    const [otherIncome, setOtherIncome] = useState(0);
    const [filing, setFiling] = useState("single"); // "single" | "married"

    const result = useMemo(() => {
        const hr = toNum(hourly);
        const rh = Math.min(40, toNum(regHours));
        const oh = toNum(otHours);
        const tipsW = toNum(weeklyTips);
        const w = Math.max(1, toNum(weeks));
        const other = toNum(otherIncome);
        const isJoint = filing === "married";

        // Annual earnings components
        const regularWages = hr * rh * w;
        const otFullPay = hr * 1.5 * oh * w; // full OT earnings (already in paycheck)
        const otPremium = hr * 0.5 * oh * w; // the "extra half" — deductible portion
        const annualTips = tipsW * w;

        const grossWages = regularWages + otFullPay + annualTips + other;

        // MAGI proxy (gross wages, since OBBBA deductions don't affect MAGI for their own phaseout)
        const magi = grossWages;
        const tipsCap = isJoint ? TIPS_CAP_JOINT : TIPS_CAP_SINGLE;
        const otCap = isJoint ? OT_CAP_JOINT : OT_CAP_SINGLE;
        const phaseStart = isJoint ? PHASEOUT_START_JOINT : PHASEOUT_START_SINGLE;

        const allowedTipsCap = phaseoutReduce(tipsCap, magi, phaseStart);
        const allowedOtCap = phaseoutReduce(otCap, magi, phaseStart);

        const tipsDeduction = Math.min(annualTips, allowedTipsCap);
        const otDeduction = Math.min(otPremium, allowedOtCap);
        const totalDeduction = tipsDeduction + otDeduction;

        // Federal income tax — before OBBBA
        const { tax: taxBefore, taxable: taxableBefore } = federalIncomeTax(grossWages, isJoint ? "married" : "single");
        // Federal income tax — after OBBBA deductions
        const adjustedGross = Math.max(0, grossWages - totalDeduction);
        const { tax: taxAfter, taxable: taxableAfter } = federalIncomeTax(adjustedGross, isJoint ? "married" : "single");

        const savings = Math.max(0, taxBefore - taxAfter);
        const fica = grossWages * FICA_RATE; // unchanged by OBBBA

        return {
            grossWages, regularWages, otFullPay, otPremium, annualTips, magi,
            tipsDeduction, otDeduction, totalDeduction, allowedTipsCap, allowedOtCap,
            taxBefore, taxAfter, taxableBefore, taxableAfter, savings, fica,
            monthlyBoost: savings / 12,
            effectiveBefore: grossWages > 0 ? (taxBefore / grossWages) * 100 : 0,
            effectiveAfter: grossWages > 0 ? (taxAfter / grossWages) * 100 : 0,
            inPhaseout: magi > phaseStart,
            phaseStart,
        };
    }, [hourly, regHours, otHours, weeklyTips, weeks, otherIncome, filing]);

    return (
        <CalculatorShell
            slug="overtime-tips"
            explainer={
                                <>
                    <h2>How this overtime &amp; tips tax calculator works</h2>
                    <p>
                        This calculator estimates the taxes on overtime pay and tip income so hourly workers
                        and service industry employees can understand their actual take-home pay. It also
                        accounts for relevant deductions under current tax provisions including OBBBA-related
                        deductions.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Overtime taxation:</strong> overtime pay is taxed at exactly the same rates as regular wages — there is no special overtime tax rate.</li>
                        <li><strong>Tip income:</strong> fully taxable as ordinary income and must be reported whether received in cash or on a card.</li>
                        <li><strong>Allocated tips:</strong> if your reported tips fall below 8% of gross sales, your employer is required to report allocated tips on your W-2.</li>
                        <li><strong>Total tax estimate:</strong> combines your regular withholding with the incremental tax on overtime and tip income.</li>
                        <li><strong>Net take-home:</strong> subtracts estimated federal income tax, state income tax, Social Security (6.2%), and Medicare (1.45%).</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>Keep a daily tip log — accurate records protect you in an audit and ensure you're not over- or under-reporting.</li>
                        <li>If you earn significant tip income, adjust your W-4 withholding to avoid a large tax bill at filing time.</li>
                        <li>Cash tips carry the same reporting obligation as card tips — they are your responsibility to report to your employer and on your return.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Hourly rate" prefix="$" value={hourly} onChange={setHourly} step={0.25} dataTestId="calc-input-hourly" />
                        <Field label="Regular hrs / week" value={regHours} onChange={setRegHours} max={40} dataTestId="calc-input-reghours" hint="Capped at 40" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Overtime hrs / week" value={otHours} onChange={setOtHours} dataTestId="calc-input-othours" hint="Paid at 1.5×" />
                        <Field label="Tips / week" prefix="$" value={weeklyTips} onChange={setWeeklyTips} dataTestId="calc-input-tips" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Weeks worked / year" value={weeks} onChange={setWeeks} max={52} dataTestId="calc-input-weeks" />
                        <Field label="Other annual income" prefix="$" value={otherIncome} onChange={setOtherIncome} dataTestId="calc-input-other" hint="Non-tip, non-OT (optional)" />
                    </div>
                    <div>
                        <label className="block font-sans text-[11px] uppercase tracking-[0.18em] text-primary/80 font-medium mb-1.5">
                            Filing status
                        </label>
                        <div className="inline-flex rounded-md border border-border overflow-hidden" role="tablist">
                            <button
                                role="tab"
                                aria-selected={filing === "single"}
                                onClick={() => setFiling("single")}
                                data-testid="calc-filing-single"
                                className={`px-4 h-11 text-sm transition-colors ${filing === "single" ? "bg-primary text-primary-foreground" : "bg-background text-primary hover:bg-muted"}`}
                            >
                                Single
                            </button>
                            <button
                                role="tab"
                                aria-selected={filing === "married"}
                                onClick={() => setFiling("married")}
                                data-testid="calc-filing-married"
                                className={`px-4 h-11 text-sm transition-colors border-l border-border ${filing === "married" ? "bg-primary text-primary-foreground" : "bg-background text-primary hover:bg-muted"}`}
                            >
                                Married filing jointly
                            </button>
                        </div>
                    </div>

                    <div className="pt-3 space-y-1.5 text-xs text-muted-foreground font-mono tabular">
                        <div className="flex justify-between"><span>Regular wages</span><span>{fmtUSD(result.regularWages, { decimals: 0 })}</span></div>
                        <div className="flex justify-between"><span>Overtime pay (full 1.5×)</span><span>{fmtUSD(result.otFullPay, { decimals: 0 })}</span></div>
                        <div className="flex justify-between"><span>Overtime <em>premium</em> (deductible)</span><span>{fmtUSD(result.otPremium, { decimals: 0 })}</span></div>
                        <div className="flex justify-between"><span>Tips (deductible)</span><span>{fmtUSD(result.annualTips, { decimals: 0 })}</span></div>
                        <div className="flex justify-between font-bold text-primary pt-1 border-t border-border"><span>Gross annual</span><span>{fmtUSD(result.grossWages, { decimals: 0 })}</span></div>
                    </div>

                    {result.inPhaseout && (
                        <div className="bg-accent/10 border border-accent/30 rounded-md p-3 text-sm text-primary" data-testid="phaseout-warning">
                            <strong>Income phaseout triggered.</strong> Your MAGI of {fmtUSD(result.magi, { decimals: 0 })} is above the {fmtUSD(result.phaseStart, { decimals: 0 })} threshold.
                            Caps reduced to {fmtUSD(result.allowedOtCap, { decimals: 0 })} (OT) and {fmtUSD(result.allowedTipsCap, { decimals: 0 })} (tips).
                        </div>
                    )}
                </div>

                <div className="lg:col-span-2 space-y-4">
                    <ResultStat
                        label="Annual federal tax savings"
                        value={result.savings}
                        format={(n) => fmtUSD(n, { decimals: 0 })}
                        secondary={[
                            { label: "Tax before OBBBA", value: result.taxBefore, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-taxbefore" },
                            { label: "Tax after OBBBA", value: result.taxAfter, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-taxafter" },
                            { label: "OT deduction", value: result.otDeduction, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-otded" },
                            { label: "Tips deduction", value: result.tipsDeduction, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-tipsded" },
                        ]}
                    />
                    <ResultStat
                        label="Extra take-home per month"
                        value={result.monthlyBoost}
                        primary={false}
                        format={(n) => fmtUSD(n, { decimals: 0 })}
                        secondary={[
                            { label: "Effective rate before", value: result.effectiveBefore, format: (n) => fmtPct(n, 1), testId: "calc-result-effbefore" },
                            { label: "Effective rate after", value: result.effectiveAfter, format: (n) => fmtPct(n, 1), testId: "calc-result-effafter" },
                        ]}
                    />
                    <div className="text-xs text-muted-foreground leading-relaxed px-1">
                        <strong className="text-primary">FICA still applies:</strong>{" "}
                        {fmtUSD(result.fica, { decimals: 0 })} in Social Security + Medicare
                        is still withheld — OBBBA only exempts federal income tax, not payroll tax.
                    </div>
                </div>
            </div>
        </CalculatorShell>
    );
}
